import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/contexts/AuthContext";
import { ThemeProvider } from "@/contexts/ThemeContext";
import ProtectedRoute from "@/components/ProtectedRoute";
import { Toaster } from "sonner";
import useScrollRestoration from "@/lib/useScrollRestoration";

import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
import ForgotPassword from "@/pages/ForgotPassword";
import ResetPassword from "@/pages/ResetPassword";
import Discover from "@/pages/Discover";
import Chats from "@/pages/Chats";
import CreateCharacter from "@/pages/CreateCharacter";
import CharacterDetail from "@/pages/CharacterDetail";
import ChatView from "@/pages/ChatView";
import Personas from "@/pages/Personas";
import Profile from "@/pages/Profile";
import Library from "@/pages/Library";
import Upgrade from "@/pages/Upgrade";
import UpgradeSuccess from "@/pages/UpgradeSuccess";
import PaymentSuccess from "@/pages/PaymentSuccess";
import PaymentCancel from "@/pages/PaymentCancel";
import PublicPricing from "@/pages/PublicPricing";
import Settings from "@/pages/Settings";
import BackupOptions from "@/pages/BackupOptions";
import AdminInbox from "@/pages/AdminInbox";
import AuthCallback from "@/pages/AuthCallback";
import PublicProfile from "@/pages/PublicProfile";
import CreatePersona from "@/pages/CreatePersona";
import AdminUsage from "@/pages/AdminUsage";
import Terms from "@/pages/legal/Terms";
import Privacy from "@/pages/legal/Privacy";
import Refunds from "@/pages/legal/Refunds";
import Contact from "@/pages/legal/Contact";

function RootRedirect() {
  const { user, initializing } = useAuth();
  if (initializing || user === null) {
    return (
      <div className="flex min-h-screen items-center justify-center">
        <div className="font-display text-2xl text-neutral-500 animate-pulse">Immersive</div>
      </div>
    );
  }
  return <Navigate to={user ? "/discover" : "/welcome"} replace />;
}

function AppRoutes() {
  // Restore scroll position when the user hits Back so lists don't jump to top.
  useScrollRestoration();

  // Global Emergent Google Auth callback interceptor. The OAuth flow returns
  // to our origin with `#session_id=...` in the URL fragment. Regardless of
  // which pathname it landed on, render AuthCallback so the exchange runs
  // before ProtectedRoute has a chance to bounce the user to /login.
  if (typeof window !== "undefined" && window.location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }

  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      <Route path="/welcome" element={<Welcome />} />
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />
      <Route path="/forgot-password" element={<ForgotPassword />} />
      <Route path="/reset-password" element={<ResetPassword />} />

      {/* Public legal pages — required for Stripe activation. Accessible without auth. */}
      <Route path="/terms" element={<Terms />} />
      <Route path="/privacy" element={<Privacy />} />
      <Route path="/refunds" element={<Refunds />} />
      <Route path="/contact" element={<Contact />} />
      {/* Public pricing page — shareable URL for Stripe (and any other
          payment processor / app-store) reviewers to see the product,
          price, and terms without needing to sign in. */}
      <Route path="/pricing" element={<PublicPricing />} />

      <Route path="/discover" element={<ProtectedRoute><Discover /></ProtectedRoute>} />
      <Route path="/chats" element={<ProtectedRoute><Chats /></ProtectedRoute>} />
      <Route path="/create" element={<ProtectedRoute><CreateCharacter /></ProtectedRoute>} />
      <Route path="/edit-character/:id" element={<ProtectedRoute><CreateCharacter /></ProtectedRoute>} />
      <Route path="/library" element={<ProtectedRoute><Library /></ProtectedRoute>} />
      <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
      <Route path="/personas" element={<ProtectedRoute><Personas /></ProtectedRoute>} />
      <Route path="/upgrade" element={<ProtectedRoute><Upgrade /></ProtectedRoute>} />
      <Route path="/upgrade/success" element={<ProtectedRoute><UpgradeSuccess /></ProtectedRoute>} />
      {/* Stripe Checkout redirect targets — must be reachable without a
          fresh login redirect since Stripe sends users here directly. */}
      <Route path="/payment/success" element={<ProtectedRoute><PaymentSuccess /></ProtectedRoute>} />
      <Route path="/payment/cancel" element={<ProtectedRoute><PaymentCancel /></ProtectedRoute>} />
      <Route path="/settings" element={<ProtectedRoute><Settings /></ProtectedRoute>} />
      <Route path="/settings/backup" element={<ProtectedRoute><BackupOptions /></ProtectedRoute>} />
      <Route path="/admin/feedback" element={<ProtectedRoute><AdminInbox /></ProtectedRoute>} />
      <Route path="/character/:id" element={<ProtectedRoute><CharacterDetail /></ProtectedRoute>} />
      <Route path="/chat/:id" element={<ProtectedRoute><ChatView /></ProtectedRoute>} />
      <Route path="/u/:username" element={<ProtectedRoute><PublicProfile /></ProtectedRoute>} />
      <Route path="/create-persona" element={<ProtectedRoute><CreatePersona /></ProtectedRoute>} />
      <Route path="/edit-persona/:id" element={<ProtectedRoute><CreatePersona /></ProtectedRoute>} />
      <Route path="/admin/usage" element={<ProtectedRoute><AdminUsage /></ProtectedRoute>} />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

export default function App() {
  return (
    <ThemeProvider>
      <AuthProvider>
      <BrowserRouter>
        <Toaster
          theme="dark"
          position="top-center"
          toastOptions={{
            style: {
              background: "rgba(22, 20, 29, 0.95)",
              border: "1px solid rgba(255,255,255,0.08)",
              color: "white",
              backdropFilter: "blur(20px)",
            },
          }}
        />
        <AppRoutes />
      </BrowserRouter>
    </AuthProvider>
    </ThemeProvider>
  );
}
